import { useEffect, useState } from 'react'
import { subscribeToGuest } from '@/app/lib/guestChannel'

/**
 * The guest-facing screen, shown fullscreen on a second monitor.
 *
 * This is the only screen paying customers read, and they read it from a metre
 * away while holding a phone. So it holds exactly one idea at a time, the QR is
 * enormous, and the instruction stays in Indonesian.
 *
 * The QR sits on white rather than on the brand colour: a coloured quiet zone
 * costs scan reliability at an angle under venue lighting, which is precisely
 * the situation it is used in.
 */
export function GuestScreen() {
  const [qr, setQr] = useState<string | null>(null)

  useEffect(
    () =>
      subscribeToGuest((message) => {
        if (message.type === 'qr.show') setQr(message.dataUrl)
        if (message.type === 'qr.hide') setQr(null)
      }),
    [],
  )

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-brand-deep px-8 text-center">
      {/* A soft vignette so the white QR panel does not float on flat colour. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, rgba(255,255,255,.16), transparent 60%)',
        }}
      />

      <div className="relative flex flex-col items-center">
        {qr ? (
          <>
            {/* The wordmark only appears here — in the idle state the heading
                already says Citaria, and stacking both just repeats it. */}
            <p className="mb-2 text-[clamp(14px,1.6vw,22px)] font-medium uppercase tracking-[0.35em] text-white/70">
              Citaria
            </p>
            <h1 className="mb-[4vh] text-[clamp(28px,4.4vw,64px)] font-bold leading-tight text-white">
              Foto kamu sudah siap
            </h1>

            <div className="rounded-[28px] bg-white p-[clamp(16px,2vw,32px)] shadow-2xl">
              <img
                src={qr}
                alt="Kode QR untuk mengunduh foto"
                className="h-[min(46vh,46vw)] w-[min(46vh,46vw)] object-contain"
              />
            </div>

            <p
              className="mt-[4vh] max-w-[22ch] text-[clamp(18px,2.4vw,34px)] leading-snug text-white"
              style={{ textWrap: 'balance' }}
            >
              Silahkan pindai QR untuk mendapatkan foto secara digital
            </p>
          </>
        ) : (
          <Idle />
        )}
      </div>
    </div>
  )
}

/** Between guests. Deliberately calm — this is on screen most of the evening. */
function Idle() {
  return (
    <>
      <h1 className="text-[clamp(30px,5vw,72px)] font-bold leading-tight text-white">
        Citaria Photobooth
      </h1>
      <p className="mt-4 text-[clamp(16px,2vw,28px)] text-white/75">
        Silahkan berfoto — hasilnya akan muncul di sini
      </p>
      <div className="mt-[6vh] flex gap-3" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="citaria-live h-3 w-3 rounded-full bg-white/70"
            style={{ animationDelay: `${i * 0.25}s` }}
          />
        ))}
      </div>
    </>
  )
}
